// Week/day exports: PDF via the chrome-free /print routes (browser print →
// save as PDF), CSV client-side. Every export is audit-logged via logExport
// (fire-and-forget).
import { useState } from 'react'
import type { Employee, Shift, Team } from '../lib/types'
import { downloadCsv, hoursSummaryCsv, shiftsCsv } from '../lib/csv'
import { api } from '../api/mutations'

export function ExportMenu({
  departmentId,
  departmentName,
  weekStart,
  shifts,
  teams,
  employees,
  thresholdHours,
  day,
}: {
  departmentId: string
  departmentName: string
  weekStart: string
  shifts: Shift[]
  teams: Team[]
  /** Needed for the hours-summary CSV; pass [] when the viewer can't read the roster. */
  employees: Pick<Employee, 'email' | 'workspace' | 'homeTeamId'>[]
  thresholdHours: number
  /** When set, PDF export targets the day view. */
  day?: string
}) {
  const [open, setOpen] = useState(false)
  const teamsById = new Map(teams.map((t) => [t.id, t]))

  const logIt = (scope: 'week' | 'day', format: 'pdf' | 'csv') => {
    void api.logExport({ departmentId, scope, key: scope === 'day' && day ? day : weekStart, format }).catch(() => {})
  }

  const openPrint = () => {
    const url = day ? `/print/day/${departmentId}/${day}` : `/print/week/${departmentId}/${weekStart}`
    logIt(day ? 'day' : 'week', 'pdf')
    window.open(url, '_blank', 'noopener')
    setOpen(false)
  }

  const exportShiftsCsv = () => {
    const rows = day ? shifts.filter((s) => s.date === day) : shifts
    downloadCsv(
      `callboard-${departmentId}-${day ?? weekStart}-shifts.csv`,
      shiftsCsv(rows, { departmentName, weekStart, teamsById }),
    )
    logIt(day ? 'day' : 'week', 'csv')
    setOpen(false)
  }

  const exportHoursCsv = () => {
    downloadCsv(
      `callboard-${departmentId}-${weekStart}-hours.csv`,
      hoursSummaryCsv(shifts, employees, weekStart, thresholdHours, teamsById),
    )
    logIt('week', 'csv')
    setOpen(false)
  }

  return (
    <div className="relative">
      <button className="btn-secondary !min-h-[40px]" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        Export
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-56 rounded-md border border-hairline bg-white py-1 shadow-lg">
          <button className="block w-full px-4 py-2.5 text-left hover:bg-sky/20" onClick={openPrint}>
            Print / save PDF
          </button>
          <button className="block w-full px-4 py-2.5 text-left hover:bg-sky/20" onClick={exportShiftsCsv}>
            CSV — shifts
          </button>
          {employees.length > 0 && !day && (
            <button className="block w-full px-4 py-2.5 text-left hover:bg-sky/20" onClick={exportHoursCsv}>
              CSV — hours summary
            </button>
          )}
        </div>
      )}
    </div>
  )
}
